import {
    Box, Grid, Card, CardContent, Typography, Chip, Avatar, Tooltip,
    CircularProgress
} from "@mui/material";
import MaterialTable from "material-table";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import { useState, useEffect } from "react";
import CustomToggle from "../../../Base/views/CustomToggle";
import { GetWorkLoadAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import { getUserData } from "../../../Base/helper/baseFunctions";
import Chart from "react-apexcharts";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";

// ─── Brand ─────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, sub, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 40, height: 40 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.3rem" lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                    {sub && <Typography fontSize="0.68rem" color={color} fontWeight={600}>{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const WorkloadReportNew = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const user = getUserData();
    const isGroupOrg = user?.grouporg_flow === true;

    const [userData, setUserData] = useState({ load: true, values: [] });
    const [filters, setFilters] = useState({ primary_recruiter_filter: "" });

    const { atsWorkLoadData, isWorkLoadLoading } = useSelector(state => state.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData } = useSelector(state => state.AtsReducer?.AtsReportReducer);

    // ── Load recruiters once ──────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) setUserData({ load: false, values: atsRecruiterData });
    }, [atsRecruiterData]);

    // ── Fetch workload data ───────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetWorkLoadAction({
            from_date: null,
            to_date: null,
            primary_recruiter: filters.primary_recruiter_filter
                ? `{${filters.primary_recruiter_filter.ats_users_id}}`
                : null,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleFilter = (event, value) => {
        if (event === "primary_recruiter_filter") {
            setFilters(f => ({ ...f, primary_recruiter_filter: value || "" }));
        }
    };

    // ── Aggregate per recruiter (flatten: multiple rows per recruiter) ─────────
    const rows = atsWorkLoadData || [];

    const recruiterMap = {};
    rows?.forEach(r => {
        const name = r.recruiter_name || "Unknown";
        if (!recruiterMap[name]) recruiterMap[name] = { jobs: 0, positions: 0, clients: [] };
        recruiterMap[name].jobs += parseInt(r.no_of_jobs) || 0;
        recruiterMap[name].positions += parseInt(r.no_of_positions) || 0;
        if (r.company_name && !recruiterMap[name].clients.includes(r.company_name)) {
            recruiterMap[name].clients.push(r.company_name);
        }
    });

    const recruiterList = Object.entries(recruiterMap)
        ?.sort(([, a], [, b]) => b.jobs - a.jobs);

    // KPIs
    const totalRecruiters = recruiterList.length;
    const totalJobs = Object.values(recruiterMap)?.reduce((s, r) => s + r.jobs, 0) || 0;
    const totalPositions = Object.values(recruiterMap)?.reduce((s, r) => s + r.positions, 0) || 0;
    const avgJobs = totalRecruiters > 0 ? Math.round(totalJobs / totalRecruiters) : 0;
    const aboveAvg = recruiterList?.filter(([, v]) => v.jobs > avgJobs).length || 0;
    const belowAvg = totalRecruiters - aboveAvg;

    // ── Horizontal bar: jobs per recruiter ────────────────────────────────────
    const top12 = recruiterList.slice(0, 12);
    const workloadBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: top12.map(([, v]) => v.jobs > avgJobs ? BRAND : "#E15759"),
            plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, barHeight: "55%" } },
            dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 600 } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
            xaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", fontWeight: 500 } } },
            legend: { show: false },
            annotations: avgJobs > 0 ? {
                xaxis: [{
                    x: avgJobs, borderColor: "#F59E0B", strokeDashArray: 4,
                    label: { text: `Avg: ${avgJobs}`, orientation: "vertical", style: { fontSize: "10px", color: "#D97706", background: "#FEF3C7" } }
                }]
            } : {},
            tooltip: { y: { formatter: v => `${v} JDs` } },
        },
        series: [{ data: top12.map(([name, v]) => ({ x: name.split(" ")[0], y: v.jobs })) }],
    };

    // ── Donut: above vs below average ─────────────────────────────────────────
    const splitDonut = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: ["Above Average", "Below Average"],
            colors: [BRAND, "#E15759"],
            dataLabels: { enabled: false },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: {
                pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Recruiters", fontSize: "12px", color: "#94A3B8" } } } },
            },
        },
        series: [aboveAvg || 0, belowAvg || 0],
    };

    // ── Grouped bar: Jobs vs Positions per recruiter ───────────────────────────
    const top8 = recruiterList.slice(0, 8);
    const groupedBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND, "#10B981"],
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: {
                categories: top8.map(([n]) => n.split(" ")[0]),
                labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Open JDs", data: top8.map(([, v]) => v.jobs) },
            { name: "Open Positions", data: top8.map(([, v]) => v.positions) },
        ],
    };

    // ── Table columns (same fields as existing WorkloadReports.js) ─────────────
    const columns = [
        { title: "Recruiter", field: "recruiter_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        { title: isGroupOrg ? "Group Org" : "Client", field: "company_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        {
            title: "Open JDs", field: "no_of_jobs", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Chip label={parseInt(r.no_of_jobs) || 0} size="small"
                    sx={{ bgcolor: (parseInt(r.no_of_jobs) || 0) > 0 ? "#F3EDFD" : "#F1F5F9", color: (parseInt(r.no_of_jobs) || 0) > 0 ? BRAND : "#94A3B8", fontWeight: 700 }}
                />
            ),
        },
        {
            title: "Open Positions", field: "no_of_positions", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Chip label={parseInt(r.no_of_positions) || 0} size="small"
                    sx={{ bgcolor: (parseInt(r.no_of_positions) || 0) > 0 ? "#ECFDF5" : "#F1F5F9", color: (parseInt(r.no_of_positions) || 0) > 0 ? "#059669" : "#94A3B8", fontWeight: 700 }}
                />
            ),
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters & View Toggle ─────────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Recruiter"
                            value={filters.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name"
                            required={false}
                            variant="outlined"
                            name="primary_recruiter_filter"
                            onChangeEvent={handleFilter}
                            data={userData}
                            size="small"
                        />
                    </Grid>
                    {isWorkLoadLoading && (
                        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={22} disableShrink/>
                        </Grid>
                    )}
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <CustomToggle
                                isChecked={!localIsDashboardView}
                                handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                Yestext={"Table"}
                                NoText={"Dashboard"}
                                YesTooltip={"Switch to Table View"}
                                NoTooltip={"Switch to Dashboard View"}
                                style={{ height: "37px", labelPadding: "2px 8px" }}
                            />
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<GroupsOutlinedIcon sx={{ fontSize: 20 }} />} value={totalRecruiters} label="Total Recruiters" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<BalanceOutlinedIcon sx={{ fontSize: 20 }} />} value={avgJobs} label="Avg Workload" sub="JDs per recruiter" color={BRAND} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<TrendingUpIcon sx={{ fontSize: 20 }} />} value={aboveAvg} label="Above Average" sub={`${totalJobs} total JDs`} color="#10B981" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<WarningAmberIcon sx={{ fontSize: 20 }} />} value={belowAvg} label="Below Average" sub={`${totalPositions} total positions`} color="#E15759" />
                            </Grid>
                        </Grid>

                        {/* ── Charts ────────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Workload Distribution</Typography>
                                        <Typography variant="caption" color="text.secondary">Open JDs per recruiter (🟣 above avg · 🔴 below avg)</Typography>
                                        {top12.length > 0 ? (
                                            <Chart options={workloadBar.options} series={workloadBar.series} type="bar" height={280} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 250, color: "#94A3B8", flexDirection: "column", gap: 1 }}>
                                                <WorkOutlineOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                                                <Typography fontSize="0.82rem">No workload data found</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Balance Split</Typography>
                                        <Typography variant="caption" color="text.secondary">Recruiters above vs below average</Typography>
                                        {totalRecruiters > 0 ? (
                                            <Chart options={splitDonut.options} series={splitDonut.series} type="donut" height={280} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 250, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>JDs vs Positions</Typography>
                                        <Typography variant="caption" color="text.secondary">Open JDs and positions per recruiter (top 8)</Typography>
                                        {top8.length > 0 ? (
                                            <Chart options={groupedBar.options} series={groupedBar.series} type="bar" height={280} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 250, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Recruiter Summary Cards ───────────────────────────── */}
                        <Card>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                    <Typography variant="subtitle2" fontWeight={700}>Recruiter Workload Details</Typography>
                                    <Chip label={`${totalRecruiters} recruiters · ${totalJobs} open JDs`} size="small" sx={{ bgcolor: "#F3EDFD", color: BRAND, fontWeight: 600, fontSize: "0.68rem" }} />
                                </Box>
                                <Grid container spacing={1.2}>
                                    {recruiterList.map(([name, stats], i) => {
                                        const isAbove = stats.jobs > avgJobs;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={name}>
                                                <Tooltip title={stats.clients.length > 0 ? `Clients: ${stats.clients.join(", ")}` : "No clients"} placement="top">
                                                    <Card variant="outlined" sx={{
                                                        borderColor: "rgba(0,0,0,0.08)",
                                                        cursor: "default",
                                                        transition: "all 0.2s",
                                                        "&:hover": { borderColor: BRAND, boxShadow: `0 4px 14px ${BRAND}20`, transform: "translateY(-2px)" },
                                                    }}>
                                                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar sx={{ width: 36, height: 36, bgcolor: isAbove ? "#F3EDFD" : "#FEE2E2", color: isAbove ? BRAND : "#E15759", fontSize: "0.75rem", fontWeight: 700 }}>
                                                                {name.substring(0, 2).toUpperCase()}
                                                            </Avatar>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight={650} fontSize="0.82rem" noWrap>{name}</Typography>
                                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                                    {stats.clients.length} client{stats.clients.length !== 1 ? "s" : ""}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                                                <Chip label={`${stats.jobs} JDs`} size="small" sx={{ bgcolor: isAbove ? "#D1FAE5" : "#FEE2E2", color: isAbove ? "#059669" : "#DC2626", fontWeight: 700 }} />
                                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3 }}>
                                                                    {stats.positions} positions
                                                                </Typography>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Tooltip>
                                            </Grid>
                                        );
                                    })}
                                    {recruiterList.length === 0 && (
                                        <Grid item xs={12}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, color: "#94A3B8", gap: 1 }}>
                                                <WorkOutlineOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                                                <Typography fontSize="0.85rem">No workload data found</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </>
                ) : (
                    /* ── Detail Table ──────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <MaterialTable
                            columns={columns}
                            data={rows}
                            options={{
                                maxBodyHeight: "calc(100vh - 280px)",
                                minBodyHeight: "350px",
                                sorting: true,
                                search: true,
                                searchFieldAlignment: "right",
                                searchAutoFocus: true,
                                searchFieldVariant: "standard",
                                filtering: false,
                                exportButton: true,
                                exportAllData: true,
                                paging: true,
                                pageSizeOptions: [25, 50, 100],
                                pageSize: 25,
                                actionsColumnIndex: -1,
                                draggable: false,
                                columnsButton: true,
                                grouping: true,
                                headerStyle: {
                                    backgroundColor: "#fff",
                                    color: "#64748B",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    whiteSpace: "nowrap",
                                    padding: "10px",
                                },
                            }}
                            title="Workload Detail"
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default WorkloadReportNew;
